import { useEffect, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      articles
        .filter((a) => a.slug !== slug && a.category === article?.category)
        .slice(0, 3),
    [slug, article]
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-search-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 mb-4">文章不存在或已被删除</p>
            <Link
              to="/"
              className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: 'PG模拟器',
      url: 'https://www.pg-simulator.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章资讯', path: '/article/pg-monitiqi-xinshou-gonglue' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto py-10">
          <div className="mb-8">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span><i className="ri-user-line mr-1"></i>{article.author}</span>
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
            </div>
          </div>

          <div className="w-full aspect-[2/1] rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - pg模拟器`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
              [&_h3]:font-heading [&_h3]:text-lg [&_h3]:text-foreground-800 [&_h3]:mt-6 [&_h3]:mb-3
              [&_p]:mb-4 [&_p]:text-foreground-700
              [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1.5
              [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1.5
              [&_strong]:text-foreground-900 [&_strong]:font-medium"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-2 mt-10 pt-6 border-t border-background-200 flex-wrap">
            <span className="text-sm text-foreground-500 mr-2">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 max-w-4xl mx-auto pb-16">
            <h2 className="font-heading text-xl text-foreground-900 mb-6">相关文章</h2>
            <div className="flex flex-col gap-4">
              {relatedArticles.map((a) => (
                <ArticleCard
                  key={a.slug}
                  slug={a.slug}
                  title={a.title}
                  excerpt={a.excerpt}
                  category={a.category}
                  date={a.date}
                  readTime={a.readTime}
                  image={a.image}
                />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}